import { useTheme } from '@ytl/common-web';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useApp } from '../../contexts/appContext';

const PORTAL_STYLE_ID = 'ytl-alm-recaptcha-portal-styles';
const PORTAL_ATTR = 'data-ytl-alm-recaptcha-portal';
const PORTAL_STYLES = `
[${PORTAL_ATTR}] {
  z-index: 1999999999;
}
[${PORTAL_ATTR}="invisible"] {
  position: fixed;
  width: 0;
  height: 0;
  overflow: hidden;
}
[${PORTAL_ATTR}="visible"] {
  position: absolute;
}
`;

/** Inject portal styles into document.head once */
const ensurePortalStyles = () => {
  if (document.getElementById(PORTAL_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PORTAL_STYLE_ID;
  style.textContent = PORTAL_STYLES;
  document.head.appendChild(style);
};

type RecaptchaPortalProps = {
  recaptchaRef: React.RefObject<ReCAPTCHA> | React.MutableRefObject<ReCAPTCHA | null>;
  sitekey: string;
  hl: string;
  onChange: (token: string | null) => void;
  className?: string;
  asyncScriptOnLoad?: () => void;
  size?: 'compact' | 'normal' | 'invisible';
  /** Reference to the element in shadow DOM where a visible captcha should be positioned next to */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Override theme from initParams ('dark' | 'light'). Takes priority over useTheme().isDark */
  themeOverride?: 'dark' | 'light';
  /** Override language from initParams ('hu' | 'en'). Takes priority over the hl prop */
  languageOverride?: 'hu' | 'en';
};

/**
 * Renders reCAPTCHA outside of Shadow DOM when in widget mode.
 *
 * Google reCAPTCHA iframes (from google.com) need to access `window` properties
 * on the host page. Inside a Shadow DOM web component embedded cross-origin,
 * the browser blocks this with a SecurityError. Portaling to document.body
 * avoids the shadow boundary entirely.
 */
export const RecaptchaPortal: React.FC<RecaptchaPortalProps> = ({
  recaptchaRef,
  sitekey,
  hl,
  onChange,
  className,
  asyncScriptOnLoad,
  size,
  anchorRef,
  themeOverride,
  languageOverride,
}) => {
  const { isWidgetMode } = useApp();
  const { isDark } = useTheme();
  const resolvedTheme = themeOverride || (isDark ? 'dark' : 'light');
  const resolvedLang = languageOverride || hl;
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const containerCreatedRef = useRef(false);
  const placeholderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isWidgetMode) return;

    ensurePortalStyles();

    // Create a container div in document.body for the reCAPTCHA
    const container = document.createElement('div');
    const variant = size === 'invisible' ? 'invisible' : 'visible';
    container.setAttribute(PORTAL_ATTR, variant);

    document.body.appendChild(container);
    containerCreatedRef.current = true;
    setPortalContainer(container);

    return () => {
      containerCreatedRef.current = false;
      setPortalContainer(null);
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
  }, [isWidgetMode, size]);

  // For visible captcha in widget mode, position the portal container
  // absolutely relative to the anchor/placeholder element in the shadow DOM
  useEffect(() => {
    if (!isWidgetMode || !portalContainer || size === 'invisible') return;

    const anchor = anchorRef?.current ?? placeholderRef.current;
    if (!anchor) return;

    const updatePosition = () => {
      const rect = anchor.getBoundingClientRect();
      // Center the portal horizontally within the placeholder's width
      // reCAPTCHA standard widget width is 304px
      const recaptchaWidth = 304;
      const centeredLeft = rect.left + (rect.width - recaptchaWidth) / 2;
      portalContainer.style.left = `${centeredLeft + window.scrollX}px`;
      portalContainer.style.top = `${rect.top + window.scrollY}px`;
    };

    updatePosition();

    // Re-position on scroll/resize so it stays aligned
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isWidgetMode, portalContainer, size, anchorRef]);

  const recaptchaElement = (
    <ReCAPTCHA
      ref={recaptchaRef}
      sitekey={sitekey}
      hl={resolvedLang}
      onChange={onChange}
      className={className}
      asyncScriptOnLoad={asyncScriptOnLoad}
      size={size}
      theme={resolvedTheme}
    />
  );

  // In widget mode, portal to document.body to escape shadow DOM
  if (isWidgetMode && portalContainer) {
    return (
      <>
        {/* Placeholder sits in the original DOM position so the portal can track where to appear */}
        {size !== 'invisible' && (
          <div
            ref={placeholderRef}
            className={className}
            style={{ minHeight: 78 }}
          />
        )}
        {createPortal(recaptchaElement, portalContainer)}
      </>
    );
  }

  // In normal (non-widget) mode, render inline as before
  return recaptchaElement;
};
